import { useState, useEffect } from "react";
import { HeadingLarge, LabelMedium } from "../components/OnboardingTypography";
import { OnboardingButtonPrimary } from "../components/OnboardingButton";
import OnboardingSetupForm from "../components/OnboardingSetupForm";
import OnboardingConnectFinancialSystem from "../components/OnboardingConnectFinancialSystem";
import OnboardingSignUserAgreement from "../components/OnboardingSignUserAgreement";
import OnboardingHelpLink from "../components/OnboardingHelpLink";
import { useIntegrationFields } from "../../hooks/useIntegrationFields";
import {
  FlexColumn,
  MessageBox,
  THEME,
  CenteredContainer,
  ButtonSection,
  DisabledButtonState,
} from "../components/OnboardingLayoutComponents";
import { useFinancialSystems } from "../hooks/useFinancialSystems";

const OnboardingSetupContainer = ({
  onboardingData,
  saving,
  error,
  completionSuccess = false,
  saveSignatureProgress,
  saveSetupInfo,
  saveAccountingSystemInfo,
  completeSetup,
  onCreateKosCreditor,
  kosCreditorLoading = false,
  kosCreditorError = null,
  onSetupIntegration,
  integrationSetupLoading = false,
  integrationSetupError = null,
  signingInitLoading = false,
  signingInitError = null,
  submissionId = null,
  skipSignature = false,
}) => {
  const { getSystemKey } = useFinancialSystems();
  // State for the three fields that OnboardingSetupForm actually handles
  const [accountNumber, setAccountNumber] = useState(
    onboardingData?.accountNo || ""
  );
  const [selectedService, setSelectedService] = useState(
    onboardingData?.defaultProductNo?.toString() || ""
  );
  const [mva, setMva] = useState(onboardingData?.vatRequired ? "Ja" : "Nei");

  // State for financial system (OnboardingConnectFinancialSystem)
  const [selectedFinancialSystem, setSelectedFinancialSystem] = useState(
    onboardingData ? onboardingData.clientSystem ?? "" : ""
  );
  const [otherFinancialSystemName, setOtherFinancialSystemName] = useState(
    onboardingData?.otherFinancialSystemName || ""
  );

  // Integration fields state
  const [integrationSettings, setIntegrationSettings] = useState(
    onboardingData?.integrationSettings || {}
  );
  const {
    integrationFields,
    loading: integrationLoading,
    error: integrationError,
    fetchFields,
    clearFields,
  } = useIntegrationFields();

  // Update local state when onboardingData changes
  useEffect(() => {
    if (onboardingData) {
      setAccountNumber(onboardingData.accountNo || "");
      setSelectedService(onboardingData.defaultProductNo?.toString() || "");
      setMva(onboardingData.vatRequired ? "Ja" : "Nei");
      setSelectedFinancialSystem(onboardingData.clientSystem);
      setOtherFinancialSystemName(onboardingData.otherFinancialSystemName || "");
      setIntegrationSettings(onboardingData.integrationSettings || {});
    }
  }, [onboardingData]);

  // Fetch integration fields when financial system changes
  useEffect(() => {
    if (selectedFinancialSystem && selectedFinancialSystem !== null) {
      const systemKey = getSystemKey(selectedFinancialSystem);
      if (systemKey) {
        fetchFields(systemKey);
      }
    } else {
      // Clear integration fields when "Annet" is selected (selectedFinancialSystem === null)
      clearFields();
    }
  }, [selectedFinancialSystem, getSystemKey, fetchFields, clearFields]);

  // Initialize default values for integration fields when both fields and data are loaded
  useEffect(() => {
    if (
      integrationFields &&
      Object.keys(integrationFields).length > 0 &&
      onboardingData
    ) {
      setIntegrationSettings((prev) => {
        const currentSettings = prev || {};
        const defaultValues = {};
        let hasDefaults = false;

        Object.entries(integrationFields).forEach(([fieldKey, fieldConfig]) => {
          // Only set default if not already set in integrationSettings
          if (!currentSettings[fieldKey] && fieldConfig.defaultValue) {
            defaultValues[fieldKey] = fieldConfig.defaultValue;
            hasDefaults = true;
          }
        });

        if (hasDefaults) {
          return {
            ...currentSettings,
            ...defaultValues,
          };
        }

        return currentSettings;
      });
    }
  }, [integrationFields, onboardingData]); // No need for integrationSettings in dependencies

  // Validate integration completion TODO: This should be used below it think?
  const validateIntegrationComplete = () => {
    if (!selectedFinancialSystem) {
      return true; // Integration is optional
    }

    const requiredFields = Object.entries(integrationFields)
      .filter(([_, field]) => field.required)
      .map(([key, _]) => key);

    return requiredFields.every(
      (field) =>
        integrationSettings[field] && integrationSettings[field].trim() !== ""
    );
  };

  // Handle integration field changes
  const handleIntegrationChange = (fieldName, value) => {
    setIntegrationSettings((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Helper function to get the logical step number based on what's visible
  const getLogicalStepNumber = (physicalStep) => {
    if (skipSignature) {
      // When signature is skipped, shift step numbers down
      return physicalStep - 1;
    }
    return physicalStep;
  };

  // Helper functions to determine step status
  const getStepStatus = (stepNumber) => {
    const hasSignedAgreement =
      onboardingData?.hasSignedAgreement === true ||
      onboardingData?.signingStatus === "Completed";
    const kosCreditorCreated = onboardingData?.kosCreditorCreated === true;
    const integrationSetupCompleted =
      onboardingData?.integrationSetupCompleted === true;

    // In behalf mode, signature is automatically completed
    const signatureCompleted = skipSignature ? true : hasSignedAgreement;

    switch (stepNumber) {
      case 1:
        if (signatureCompleted) return "completed";
        return "current";
      case 2:
        if (kosCreditorCreated) return "completed";
        if (signatureCompleted) return "current";
        return "pending";
      case 3:
        if (integrationSetupCompleted) return "completed";
        if (kosCreditorCreated) return "current";
        return "pending";
      default:
        return "pending";
    }
  };

  // Check if user can complete setup
  const canCompleteSetup = () => {
    // Check signing completion - support both old and new signing flow
    const hasSignedAgreement =
      onboardingData?.hasSignedAgreement === true ||
      onboardingData?.signingStatus === "Completed";
    const kosCreditorCreated = onboardingData?.kosCreditorCreated === true;

    // In behalf mode, signature is skipped so we don't require it
    const signatureRequirement = skipSignature ? true : hasSignedAgreement;
    
    // All users need signed agreement (unless skipped) and KOS creditor
    if (!signatureRequirement || !kosCreditorCreated) {
      return false;
    }

    // Check integration requirements based on current selectedFinancialSystem
    if (
      selectedFinancialSystem === null ||
      Object.keys(integrationFields).some((x) => x === "noconnect")
    ) {
      // "Annet" selected - no integration required, can complete setup
      return true;
    } else if (
      selectedFinancialSystem &&
      Object.keys(integrationFields).length > 0
    ) {
      // Financial system with integration fields - check if integration is completed
      const integrationSetupCompleted =
        onboardingData?.integrationSetupCompleted === true;
      if (!integrationSetupCompleted) {
        return false;
      }
    }
    // Financial system selected but no integration fields - can complete setup

    return true;
  };

  const isSetupComplete = () => {
    return onboardingData?.isCompleted === true;
  };

  if (isSetupComplete()) {
    return (
      <CenteredContainer>
        <HeadingLarge>Registrering allerede fullført!</HeadingLarge>
        <LabelMedium style={{ marginTop: THEME.spacing.lg }}>
          Din registrering er allerede fullført. Du har tilgang til portalen.
        </LabelMedium>
      </CenteredContainer>
    );
  }

  return (
    <div>
      <FlexColumn>
        <FlexColumn
          gap={THEME.spacing.xxxl}
          padding={"0 0 " + THEME.spacing.mega + " 0"}
        >
          <HeadingLarge style={{ paddingTop: THEME.spacing.xxl }}>
            Hei {onboardingData?.webDisplayName || onboardingData?.email}, du er
            straks klar!
          </HeadingLarge>
          <LabelMedium>
            La oss signere avtalen for bruk av Kreditorforenings tjenester og så
            kan vi raskt koble oss på ditt økonomisystem.
          </LabelMedium>

          <OnboardingHelpLink />
        </FlexColumn>

        {error && (
          <MessageBox variant="error" margin={THEME.spacing.lg + " 0 0 0"}>
            {error}
          </MessageBox>
        )}

        <FlexColumn gap={THEME.spacing.xxl}>
          {/* Signature Section - Conditionally rendered based on behalf mode */}
          {!skipSignature && (
            <OnboardingSignUserAgreement
              hasSignedAgreement={onboardingData?.hasSignedAgreement || false}
              signingStatus={onboardingData?.signingStatus}
              signingUrl={onboardingData?.signingUrl}
              agreementDocumentUrl={onboardingData?.agreementDocumentUrl}
              onSignatureChange={saveSignatureProgress}
              signingInitLoading={signingInitLoading}
              signingInitError={signingInitError}
            />
          )}

          {/* Setup Form - only handles accountNumber, selectedService, mva */}
          <OnboardingSetupForm
            companyName={onboardingData?.companyName || ""}
            accountNumber={accountNumber}
            setAccountNumber={setAccountNumber}
            selectedService={selectedService}
            setSelectedService={setSelectedService}
            mva={mva}
            setMva={setMva}
            onCreateKosCreditor={onCreateKosCreditor}
            kosCreditorCreated={onboardingData?.kosCreditorCreated || false}
            kosCreditorLoading={kosCreditorLoading}
            kosCreditorError={kosCreditorError}
            hasSignedAgreement={
              onboardingData?.hasSignedAgreement === true ||
              onboardingData?.signingStatus === "Completed"
            }
            skipSignature={skipSignature}
            stepStatus={getStepStatus(skipSignature ? 1 : 2)}
            stepNumber={skipSignature ? 1 : 2}
          />

          {/* Financial System and Integration */}
          <OnboardingConnectFinancialSystem
            selectedFinancialSystem={selectedFinancialSystem}
            setSelectedFinancialSystem={setSelectedFinancialSystem}
            otherFinancialSystemName={otherFinancialSystemName}
            setOtherFinancialSystemName={setOtherFinancialSystemName}
            integrationFields={integrationFields}
            integrationSettings={integrationSettings}
            onIntegrationChange={handleIntegrationChange}
            integrationLoading={integrationLoading}
            integrationError={integrationError}
            onSetupIntegration={onSetupIntegration}
            integrationSetupCompleted={
              onboardingData?.integrationSetupCompleted || false
            }
            integrationSetupLoading={integrationSetupLoading}
            integrationSetupError={integrationSetupError}
            integrationRequired={
              selectedFinancialSystem &&
              selectedFinancialSystem !== null &&
              Object.keys(integrationFields).length > 0
            }
            saveAccountingSystemInfo={saveAccountingSystemInfo}
            setIntegrationSettings={setIntegrationSettings}
            kosCreditorCreated={onboardingData?.kosCreditorCreated || false}
            stepStatus={getStepStatus(skipSignature ? 2 : 3)}
            stepNumber={skipSignature ? 2 : 3}
          />

          {/* Complete Setup Button */}
          <ButtonSection>
            <DisabledButtonState disabled={!canCompleteSetup() || saving}>
              <OnboardingButtonPrimary
                onClick={completeSetup}
                disabled={!canCompleteSetup() || saving}
              >
                <LabelMedium>
                  {saving
                    ? "Fullfører oppsett..."
                    : "Fullfør oppsett og logg inn på nytt"}
                </LabelMedium>
              </OnboardingButtonPrimary>
            </DisabledButtonState>

            {!canCompleteSetup() && (
              <LabelMedium
                style={{
                  marginTop: THEME.spacing.sm,
                  color: THEME.colors.textMuted,
                  fontSize: THEME.fontSize.sm,
                }}
              >
                {!(
                  onboardingData?.hasSignedAgreement ||
                  onboardingData?.signingStatus === "Completed"
                )
                  ? "Du må signere avtalen før du kan fullføre registreringen"
                  : !onboardingData?.kosCreditorCreated
                  ? "Du må lagre bedriftsinformasjonen før du kan fullføre registreringen"
                  : onboardingData?.integrationRequired &&
                    !onboardingData?.integrationSetupCompleted
                  ? "Du må fullføre integrasjonskonfigurasjonen før du kan fortsette"
                  : "Vennligst fullfør alle påkrevde steg før du fortsetter"}
              </LabelMedium>
            )}

            {completionSuccess && (
              <MessageBox
                variant="success"
                margin={THEME.spacing.lg + " 0 0 0"}
                style={{ textAlign: "center" }}
              >
                <LabelMedium style={{ fontWeight: "600" }}>
                  🎉 Registrering fullført!
                </LabelMedium>
                <LabelMedium style={{ marginTop: THEME.spacing.sm }}>
                  Du blir automatisk videresendt til portalen om få sekunder...
                </LabelMedium>
              </MessageBox>
            )}
          </ButtonSection>
        </FlexColumn>
      </FlexColumn>
    </div>
  );
};

export default OnboardingSetupContainer;
